import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames/bind';
import { nanoid } from 'nanoid';

import { Svg } from 'components/svg/Svg';

import { Message } from './Message';

import s from './Select.module.scss';

const c = classNames.bind(s);

export type SelectOptions = Array<{ title: string; value: string | number }>;

type TSelectProps = {
  options: SelectOptions;
  error?: boolean;
  message?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
};

export const Select = ({
  className,
  label,
  error,
  message,
  options,
  onChange,
  disabled,
  id,
  required,
  defaultValue,
  ...rest
}: TSelectProps & React.SelectHTMLAttributes<HTMLSelectElement>) => {
  const selectId = id ?? nanoid(10);
  const [isChanged, setChanged] = useState(defaultValue ? true : false);

  const renderedLabel = required && label ? `${label} *` : label;

  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (onChange) {
      onChange(e);
    }

    setChanged(e.target.value.length > 0);
  };

  return (
    <div
      className={c(s.select, className, {
        hasLabel: typeof renderedLabel === 'string' && renderedLabel.length > 0,
        active: isChanged,
        error,
        disabled,
      })}
    >
      <div className={s.select__inner}>
        <label className={s.select__label} htmlFor={selectId}>
          {renderedLabel}
        </label>
        <select
          className={c(s.select__select)}
          {...rest}
          id={selectId}
          defaultValue={defaultValue || ''}
          onChange={handleOnChange}
          disabled={disabled}
          aria-describedby={message ? `${selectId}-error` : ''}
        >
          <option value="">
            {renderedLabel ?? (
              <FormattedMessage id="select" defaultMessage="Velja" />
            )}
          </option>
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {options &&
            options.map((option, i) => (
              <option value={option.value} key={i}>
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {option.title ?? option.value}
              </option>
            ))}
        </select>
        <Svg id="chevron" className={s.select__chevron} />
      </div>
      {message && <Message id={`${selectId}-error`}>{message}</Message>}
    </div>
  );
};
